import type * as React from "react";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
    title: React.ReactNode;
    /** Plain string or arbitrary JSX. Several pages pass live status chips here. */
    description?: React.ReactNode;
    actions?: React.ReactNode;
    /** Optional crumbs rendered ABOVE the title: e.g. ["Pages", "Weightloss offer"]. Last entry is the current page (not linked). */
    breadcrumbs?: Array<{ label: string; href?: string }>;
    className?: string;
}

export function PageHeader({ title, description, actions, breadcrumbs, className }: PageHeaderProps): React.ReactNode {
    return (
        <div
            className={cn(
                "flex flex-wrap items-start justify-between gap-4 border-b bg-background px-6 py-5",
                className
            )}
        >
            <div className="space-y-1">
                {breadcrumbs && breadcrumbs.length > 0 && (
                    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-muted-foreground">
                        {breadcrumbs.map((b, i) => (
                            <span key={i} className="flex items-center gap-1">
                                {i > 0 && <span aria-hidden="true">›</span>}
                                {b.href ? (
                                    <a href={b.href} className="hover:text-foreground hover:underline">{b.label}</a>
                                ) : (
                                    <span className="text-foreground/80">{b.label}</span>
                                )}
                            </span>
                        ))}
                    </nav>
                )}
                <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
                {description && (
                    <div className="text-sm text-muted-foreground">{description}</div>
                )}
            </div>
            {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
    );
}
